import React, { useMemo, useContext, useEffect } from 'react';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useMediaContent } from './useMediaContent';
import { getMediaCollectionIndex, getPreloadImages } from '../util';

const noticeContentInformationApiError = ({ slug, redirectTarget }) => {
  if (typeof window === 'undefined') return;

  if (typeof window.newrelic?.noticeError === 'function') {
    window.newrelic.noticeError(
      new Error(`Content Information API error in ${slug}`),
      {
        slug,
        redirectTarget,
        pathname: window.location.pathname
      }
    );
  }

  window.location.replace(redirectTarget);
};

const getLetterFromIndex = (index) => {
  const letter = String.fromCharCode(65 + index);
  return letter;
};

const createSlotMap = ({ staticSlotMap, cmsResponse, cmsControlledIndexes, isStatic, contentDecisions }) => {
  const slots = cmsResponse?.mediaContent?.layouts[0]?.slots || [];
  if (isStatic || slots.length === 0) return staticSlotMap;

  const cmsEditableSectionLetters = cmsControlledIndexes.map((entry) => {
    return getLetterFromIndex(entry);
  });

  // We delete all slots in the section that cms will control
  const filteredSlotMap = Object.fromEntries(
    Object.entries(staticSlotMap).filter(
      ([, value]) => !cmsEditableSectionLetters.includes(String(value.section))
    )
  );

  // rebuild the slot map
  slots.forEach((slot, slotIndex) => {
    const slotName = slot.id;
    const staticSlotReference = staticSlotMap[slotName];
    const mediaCollectionArray = slot.mediaCollections;

    // Resolve the active media collection once, for both mediaCollectionIndex and cmsTheme.
    const mediaCollectionIndex = getMediaCollectionIndex(contentDecisions, slotName, mediaCollectionArray);
    const cmsTheme = mediaCollectionArray?.[mediaCollectionIndex]?.theme;

    // this dynamically adds in the slotContentId | entryId | componentId | mediaCollectionIndex prop to each component
    // slotContentId is used for graphQL cache
    // entryId and componentId are used for impression provider
    // mediaCollectionIndex comes from the experience context content decision for this slot, defaulting to 0
    // Only set theme when the CMS actually supplies one, so an unthemed media collection
    // doesn't blow away an explicit theme already authored on this component's props.
    // GraphQL resolves a nullable, unset theme field to null (not undefined) - guard both.
    const hasCmsTheme = cmsTheme !== undefined && cmsTheme !== null;
    const componentsWithSlug = (staticSlotReference?.components || []).map((component) => ({
      ...component,
      props: {
        ...(component.props || {}),
        slotContentId: slot.entryId,
        mediaCollectionIndex,
        ...(hasCmsTheme && { theme: cmsTheme }),
        ...(slotIndex > 6 && {
          hydrator: {
            id: `${slot.id}-${slot.entryId}-hydrator-${slotIndex}`,
            className: "sui-h-full",
            scrollBuffer: 750,
            delay: 250,
            ...(component.props?.hydrator || {}),
          },
        }),
        // Set under both names since consumers disagree on which prop they read;
        // this avoids editing prop names component-by-component.
        entryId: slot.entryId,
        componentId: slot.entryId
      },
    }));
    // TODO: At the moment, this only supports 1 section.
    // Need to align if we want to support n number of sections.
    const newMapData = {
      ...staticSlotReference,
      components: componentsWithSlug,
      isCmsControlled: true,
      // This keeps the slots in order
      slot: slotIndex + 1,
      slug: `${slotName}__${slot.entryId || slotIndex}`
    };

    // CMS can return multiple slots that share the same slotName (slot.id). The map is keyed
    // by slotName, so the first occurrence keeps that key and any additional occurrences are
    // disambiguated by entryId to avoid overwriting one another.
    // const mapKey = filteredSlotMap[slotName] === undefined
    //   ? slotName
    //   : `${slotName}__${slot.entryId || slotIndex}`;

    const mapKey = `${slotName}__${slot.entryId || slotIndex}`

    filteredSlotMap[mapKey] = newMapData;
  });

  return filteredSlotMap;
};

const toFusionSectionSlotsJson = ({ slotMap, staticSectionSlots, isStatic }) => {
  if (isStatic) {
    return staticSectionSlots;
  }
  const sectionMap = {};

  Object.values(slotMap).forEach((slotConfig) => {
    const section = slotConfig?.section;
    if (!section) return;

    if (!sectionMap[section]) sectionMap[section] = [];

    sectionMap[section].push({
      ...slotConfig
    });
  });

  // Sort and normalize slot numbers per section
  Object.keys(sectionMap).forEach((section) => {
    sectionMap[section]
      .sort((a, b) => a.slot - b.slot)
      .forEach((item, index) => {
        item.slot = index + 1;
      });
  });

  return sectionMap;
};

export const useDynamicLayout = ({
  slug, // pageSlug
  entryId, // used for preview
  staticSlotMap = {},
  staticSectionSlots = {},
  grid = [],
  keepStaticLayout = false
}) => {
  const { experiment, channel } = useContext(ExperienceContext);
  const redirectTarget = useConfigService('cmsErrorRedirectTarget') || '/c/site_map';
  const contentDecisions = experiment?.decisions?.content;

  // Dynamic Section
  const cmsControlledIndexes = grid
    .map((item, idx) => (item.default && item.default.isCmsControlled ? idx : -1))
    .filter((idx) => idx !== -1);

  // Dynamic Slots(s)
  const cmsControlledSlots = Object.entries(staticSlotMap)
    .filter(([, slot]) => slot.isCmsControlled)
    .map(([slotSlug]) => slotSlug);

  const noCmsControlledContent = cmsControlledIndexes.length === 0 && cmsControlledSlots.length === 0;

  const { data, loading, error } = useMediaContent({
    slug,
    entryId,
    staticSlotMap,
    grid,
    skip: (!slug && !entryId) || noCmsControlledContent || keepStaticLayout
  });

  useEffect(() => {
    if (error) {
      noticeContentInformationApiError({ slug, redirectTarget });
    }
  }, [error, redirectTarget, slug]);

  const { dynamicSlotMap, fusionSectionSlotsJson, preloadImages } = useMemo(() => {
    const dynamicSlotMap = createSlotMap({
      staticSlotMap,
      cmsResponse: data,
      cmsControlledIndexes,
      // Note, should error be removed here for a isStatic condition?
      // This would display all slots in that section which could be a bad user experience.
      // However the case to keep error here is if we want to always show the user something
      // especially if the entire page is located inside of a single cmsControlled section.
      isStatic: noCmsControlledContent || keepStaticLayout || error,
      contentDecisions
    });
    const fusionSectionSlotsJson = toFusionSectionSlotsJson({
      slotMap: dynamicSlotMap,
      staticSectionSlots,
      // Note, should error be removed here for a isStatic condition?
      // This would display all slots in that section which could be a bad user experience.
      // However the case to keep error here is if we want to always show the user something
      // especially if the entire page is located inside of a single cmsControlled section.
      isStatic: noCmsControlledContent || keepStaticLayout || error,
    });
    const preloadImages = getPreloadImages({
      slotMap: dynamicSlotMap,
      cmsSlots: data?.mediaContent?.layouts?.[0]?.slots || [],
      channel,
      contentDecisions
    });
    return { dynamicSlotMap, fusionSectionSlotsJson, preloadImages };
  }, [data, grid, contentDecisions, channel]);

  return {
    data,
    loading,
    error,
    slotMap: dynamicSlotMap,
    sectionSlots: fusionSectionSlotsJson,
    preloadImages: preloadImages
  };
};

useDynamicLayout.dataModel = useMediaContent.dataModel;